import { RMMMScenarioPlanningModel } from '@blisspointmedia/bpm-types/dist/ScenarioPlanning';
import * as R from 'ramda';
import { currencyFormatter, numberFormatter } from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { capitalizeString } from '@blisspointmedia/bpm-types/dist/StyleUtils';

// MMM model segments (kpi/outcome) can come back null despite the typed string, so these
// normalize defensively. normalizeSegment yields a stable key for dropdown value/match
// round-trips; formatSegmentLabel renders a human-readable snake_case → Title label (with an
// em-dash fallback when the segment is missing).
export const normalizeSegment = (value: string | null | undefined): string => value ?? '';
export const formatSegmentLabel = (value: string | null | undefined): string =>
  value
    ? value
        .split('_')
        .map(word => capitalizeString(word))
        .join(' ')
    : '—';

export interface DateOption {
  date: string;
  client_facing: boolean;
  kpi: string;
  branch: string;
  output_file_version: number;
  scenarioPlanning?: RMMMScenarioPlanningModel;
}

export interface FormattedModelRunOptions {
  date: string;
  branch: string;
  client_facing: boolean;
  output_file_version: number;
  extraLabel: number;
  newest: boolean;
  prettyDate: string;
  kpis: {
    value: string;
    label: string;
    client_facing: boolean;
    realKpi: string;
    scenarioPlanning?: RMMMScenarioPlanningModel;
  }[];
}

// Sorts spend and effect data, grouped first at the channel level, then platform, then tactic / brand-nonbrand
export const spendAndEffectDataSort = (data: any[], snapChartSortValue: string): any[] => {
  type ChannelName = string;
  type PlatformName = string;
  interface SortableProps {
    name: string;
    spendOverOutcome: number;
    value: number;
    value2: number;
  }
  interface SortedPlatforms extends SortableProps {
    names: SortableProps[];
  }
  interface SortedChannels extends SortableProps {
    platforms: Record<PlatformName, SortedPlatforms>;
  }

  const sortedData = R.clone(data);
  const channelGroups: Record<ChannelName, SortedChannels> = {};

  sortedData.forEach(item => {
    if (!channelGroups[item.channelName]) {
      channelGroups[item.channelName] = {
        name: item.channelName,
        spendOverOutcome: item.spendOverOutcome,
        value: item.value,
        value2: item.value2,
        platforms: {}
      };
    } else {
      const channelGroup = channelGroups[item.channelName];
      let mathFunc;
      switch (snapChartSortValue) {
        case 'Alphabetical':
        case 'Spend to effect ratio':
        case 'Spend: High to low':
        case 'Revenue: High to low':
        case 'KPI Volume: High to low':
          mathFunc = Math.max;
          break;
        case 'Spend: Low to high':
        case 'Revenue: Low to high':
        case 'KPI Volume: Low to high':
        default:
          mathFunc = Math.min;
      }
      channelGroup.spendOverOutcome = mathFunc(
        item.spendOverOutcome,
        channelGroup.spendOverOutcome
      );
      channelGroup.value = mathFunc(item.value, channelGroup.value);
      channelGroup.value2 = mathFunc(item.value2, channelGroup.value2);
    }

    if (!channelGroups[item.channelName].platforms[item.platformName]) {
      channelGroups[item.channelName].platforms[item.platformName] = {
        name: item.platformName,
        spendOverOutcome: item.spendOverOutcome,
        value: item.value,
        value2: item.value2,
        names: [
          {
            name: item.name,
            spendOverOutcome: item.spendOverOutcome,
            value: item.value,
            value2: item.value2
          }
        ]
      };
    } else {
      const platformGroup = channelGroups[item.channelName].platforms[item.platformName];
      platformGroup.spendOverOutcome += item.spendOverOutcome;
      platformGroup.value += item.value;
      platformGroup.value2 += item.value2;
      platformGroup.names.push({
        name: item.name,
        spendOverOutcome: item.spendOverOutcome,
        value: item.value,
        value2: item.value2
      });
    }
  });

  let sortMethod: (a: SortableProps) => R.Ord;
  switch (snapChartSortValue) {
    case 'Spend to effect ratio':
      sortMethod = R.compose(R.negate, R.prop('spendOverOutcome'));
      break;
    case 'Spend: High to low':
      sortMethod = R.compose(R.negate, R.prop('value'));
      break;
    case 'Spend: Low to high':
      sortMethod = R.prop('value');
      break;
    case 'Revenue: High to low':
    case 'KPI Volume: High to low':
      sortMethod = R.compose(R.negate, R.prop('value2'));
      break;
    case 'Revenue: Low to high':
    case 'KPI Volume: Low to high':
      sortMethod = R.prop('value2');
      break;
    case 'Alphabetical':
    default:
      sortMethod = R.prop('name');
  }

  // Convert channel and platform Records to arrays
  let sortedGroups = Object.values(channelGroups).map(channel => ({
    ...channel,
    platforms: Object.values(channel.platforms)
  }));

  sortedGroups = R.sortBy(sortMethod, sortedGroups).map(channel => ({
    ...channel,
    platforms: R.sortBy(sortMethod, channel.platforms).map(platform => ({
      ...platform,
      names: R.sortBy(sortMethod, platform.names)
    }))
  }));

  const flattenedSortedDataNames: string[] = R.flatten(
    sortedGroups.map(channel =>
      channel.platforms.map(platform => platform.names.map(name => name.name))
    )
  );

  sortedData.sort(
    (a, b) => flattenedSortedDataNames.indexOf(a.name) - flattenedSortedDataNames.indexOf(b.name)
  );

  return sortedData;
};

export const GROUP_BY_OPTIONS = [
  { label: 'Channel', value: 'channel' },
  { label: 'Channel, Platform', value: 'platform' },
  { label: 'Channel, Platform, Tactic, Brand/Nonbrand', value: 'name' }
];

export const CONVERSION_PRETTY_NAME = {
  conversion: 'KPI Volume',
  revenue: 'Revenue'
};

export const CONVERSION_FORMATTER_TYPE = {
  conversion: numberFormatter,
  revenue: currencyFormatter
};

export const getBarChartContributorsData = (data: any[], activeDependencies: Set<string>): any[] =>
  data.filter(channel => activeDependencies.has(channel.name));

export const TINUITI_TEST_HOME_SERVICES = 'tinuititesthomeservices';
export const TINUITI_TEST_HOME_SERVICES_PROXY = 'leafguard';
